import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, UserPlus, Eye, EyeOff, Pencil, Trash, Loader2, Key, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Header } from "@/components/layout/Header";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StaffManagement } from "@/components/staff/StaffManagement";
import { ResetPasswordDialog } from "@/components/admin/ResetPasswordDialog";

const addUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  role: z.enum(["SUPER_ADMIN", "NURSE", "SCHOOL_HEAD"]),
  password: z.string().min(8, "Password must be at least 8 characters").optional(),
  schoolId: z.string().uuid().optional().nullable(),
}).refine((data) => {
  if ((data.role === "NURSE" || data.role === "SCHOOL_HEAD") && !data.schoolId) {
    return false;
  }
  return true;
}, {
  message: "School selection is required for this role",
  path: ["schoolId"],
});

type AddUserFormValues = z.infer<typeof addUserSchema>;

export default function Dashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [deleteUser, setDeleteUser] = useState<any>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<any>(null);
  const [blockUser, setBlockUser] = useState<any>(null);

  const form = useForm<AddUserFormValues>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      email: "",
      name: "",
      password: "",
      role: "NURSE",
      schoolId: null,
    },
  });

  const { data: users, refetch: refetchUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          email,
          role,
          school_id,
          schools (
            name
          ),
          created_at,
          is_blocked,
          blocked_at
        `);
      
      if (error) throw error;
      return data;
    },
  });

  const { data: schools, isLoading: isLoadingSchools } = useQuery({
    queryKey: ['schools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schools')
        .select('id, name');
      
      if (error) throw error;
      return data;
    },
  });

  const handleEdit = (userData: any) => {
    setEditingUser(userData);
    form.reset({
      email: userData.email,
      name: userData.name,
      role: userData.role,
      schoolId: userData.school_id,
    });
    setIsOpen(true);
  };

  const handleToggleBlock = async (userData: any) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const willBlock = !userData.is_blocked;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          is_blocked: willBlock,
          blocked_at: willBlock ? new Date().toISOString() : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userData.id);
      if (error) throw error;
      toast({
        title: "Success",
        description: `User has been ${willBlock ? 'blocked' : 'unblocked'} successfully`,
      });
      await refetchUsers();
      setBlockUser(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update user status",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (userId: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Call the edge function for complete user deletion
      const { error: edgeError } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });

      if (edgeError) {
        console.error("Edge function error:", edgeError);
        // Fallback to direct database deletion if edge function fails
        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', userId);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "User has been deleted successfully",
      });

      await refetchUsers();
      setDeleteUser(null);
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (values: AddUserFormValues) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      
      if (editingUser) {
        // Update existing profile
        // Call the edge function to update user properly
        try {
          const { error: edgeFunctionError } = await supabase.functions.invoke('update-user', {
            body: {
              userId: editingUser.id,
              email: values.email,
              name: values.name,
              role: values.role,
              schoolId: values.schoolId
            }
          });
          
          if (edgeFunctionError) {
            console.error('Edge function error:', edgeFunctionError);
            
            // Fallback to direct database update
            const { error } = await supabase
              .from('profiles')
              .update({
                email: values.email,
                name: values.name,
                role: values.role,
                school_id: values.schoolId,
                updated_at: new Date().toISOString()
              })
              .eq('id', editingUser.id);
            
            if (error) throw error;
          }
        } catch (edgeError) {
          console.error('Error calling update-user function:', edgeError);
          
          // Fallback to direct update
          const { error } = await supabase
            .from('profiles')
            .update({
              email: values.email,
              name: values.name,
              role: values.role,
              school_id: values.schoolId,
              updated_at: new Date().toISOString()
            })
            .eq('id', editingUser.id);
          
          if (error) throw error;
        }
        
        toast({
          title: "Success",
          description: "User has been updated successfully",
        });
      } else {
        // Create new user
        try {
          // Generate a UUID for user_id
          const randomUuid = crypto.randomUUID();
          
          // Try to call the edge function first
          const { error: edgeFunctionError } = await supabase.functions.invoke('create-user', {
            body: {
              email: values.email,
              password: values.password,
              name: values.name,
              role: values.role,
              schoolId: values.schoolId
            }
          });

          if (edgeFunctionError) {
            console.error('Edge function error:', edgeFunctionError);
            
            // Fallback to direct database insert with a random UUID for user_id
            const { data, error } = await supabase
              .from('profiles')
              .insert({
                email: values.email,
                name: values.name,
                role: values.role,
                school_id: values.schoolId,
                password: values.password,
                user_id: randomUuid, // Add a random UUID as user_id
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .select();
            
            if (error) throw error;
          }
        } catch (createError) {
          console.error('Error in user creation:', createError);
          throw createError;
        }
        
        toast({
          title: "Success",
          description: "User has been created successfully",
        });
      }
      
      form.reset({
        email: "",
        name: "",
        password: "",
        role: "NURSE",
        schoolId: null,
      });
      
      setIsOpen(false);
      setEditingUser(null);
      await refetchUsers();
    } catch (error: any) {
      console.error(`Error ${editingUser ? 'updating' : 'creating'} user:`, error);
      toast({
        title: "Error",
        description: error.message || `Failed to ${editingUser ? 'update' : 'create'} user. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!isSubmitting) {
      setIsOpen(open);
      if (!open) {
        form.reset({
          email: "",
          name: "",
          password: "",
          role: "NURSE",
          schoolId: null,
        });
        setEditingUser(null);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container px-4 py-6 mt-[147px]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6" />
            <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
          </div>

          <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogDescription>
                  {editingUser 
                    ? 'Update user account details.' 
                    : 'Create a new user account. The user will receive an email with instructions to set their password.'}
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="user@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {!editingUser && (
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Temporary Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input 
                                type={showPassword ? "text" : "password"}
                                placeholder="Minimum 8 characters" 
                                {...field} 
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a role" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="NURSE">Nurse</SelectItem>
                            <SelectItem value="SCHOOL_HEAD">School Head</SelectItem>
                            <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {(form.watch("role") === "NURSE" || form.watch("role") === "SCHOOL_HEAD") && (
                    <FormField
                      control={form.control}
                      name="schoolId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>School</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a school" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {schools?.map((school) => (
                                <SelectItem key={school.id} value={school.id}>
                                  {school.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {editingUser ? 'Updating User...' : 'Creating User...'}
                      </>
                    ) : (
                      editingUser ? 'Update User' : 'Create User'
                    )}
                  </Button>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-8 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold">User Management</h2>
          
          <div className="mt-6">
            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">Loading users...</span>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users?.map((user) => (
                    <TableRow key={user.id} className={user.is_blocked ? "opacity-60" : ""}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {user.name}
                          {user.is_blocked && (
                            <Badge variant="destructive" className="text-xs">Blocked</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'SUPER_ADMIN' ? 'default' : user.role === 'SCHOOL_HEAD' ? 'outline' : 'secondary'}>
                          {user.role === 'SCHOOL_HEAD' ? 'School Head' : user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>{user.schools?.name || '-'}</TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(user)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setResetPasswordUser(user)}
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setBlockUser(user)}
                            title={user.is_blocked ? "Unblock User" : "Block User"}
                          >
                            {user.is_blocked ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <Ban className="h-4 w-4 text-destructive" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteUser(user)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <div className="mt-8 rounded-lg border bg-card p-6">
          <StaffManagement />
        </div>
      </main>

      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user
              account and remove their data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteUser?.id)}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!blockUser} onOpenChange={() => setBlockUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {blockUser?.is_blocked ? 'Unblock this user?' : 'Block this user?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {blockUser?.is_blocked
                ? `${blockUser?.name} will be able to sign in again.`
                : `${blockUser?.name} will be signed out and prevented from signing in until unblocked.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleToggleBlock(blockUser)}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : (blockUser?.is_blocked ? "Unblock" : "Block")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ResetPasswordDialog
        open={!!resetPasswordUser}
        onOpenChange={(open) => !open && setResetPasswordUser(null)}
        user={resetPasswordUser || { id: '', name: '', email: '' }}
      />
    </div>
  );
}
